/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "./DeviceModel";

/**
 * Coarse memory classification used to size memory-hungry caches.
 *
 * Two tiers rather than three, deliberately: the only decision any caller
 * makes today is "be conservative" versus "use the defaults", and a middle
 * tier would be an untested third configuration to maintain.
 */
export type TDeviceMemoryTier = "low" | "standard";

/** No TV reports 64 GiB, so a value at or below this is already in GiB. */
const MAX_PLAUSIBLE_GIB: number = 64;

/** No TV reports 64 MiB, so a value at or below this is in MiB. */
const MAX_PLAUSIBLE_MIB: number = 65536;

const BYTES_PER_GIB: number = 1024 * 1024 * 1024;
const MIB_PER_GIB: number = 1024;
const KIB_PER_GIB: number = 1024 * 1024;

/** At or below this reported size a device is treated as memory-constrained. */
const LOW_MEMORY_THRESHOLD_GIB: number = 1.5;

/**
 * Normalise the loosely-typed device memory report into GiB.
 *
 * `TvProperties.getDeviceTotalMemory()` has three possible sources with three
 * different unit conventions, and returns the literal string "Unknown" when
 * none of them answered:
 *   - `oTvProperties.deviceTotalMemory` from the TV shell — usually MiB,
 *     occasionally raw bytes, and may arrive as a suffixed string.
 *   - `navigator.deviceMemory` — GiB, and only 0.25 … 8.
 *   - "Unknown".
 *
 * Magnitude bands separate the numeric cases unambiguously because the ranges
 * do not overlap for any real device.
 *
 * @param raw - The value reported by getDeviceTotalMemory().
 * @returns Total memory in GiB, or null when it cannot be determined.
 */
function normaliseTotalMemoryToGiB(raw: string | number): number | null {
  if (typeof raw === "number") {
    if (!Number.isFinite(raw) || raw <= 0) {
      return null;
    }
    if (raw <= MAX_PLAUSIBLE_GIB) {
      return raw;
    }
    if (raw <= MAX_PLAUSIBLE_MIB) {
      return raw / MIB_PER_GIB;
    }
    return raw / BYTES_PER_GIB;
  }

  const text = raw.trim().toLowerCase();
  if (text === "" || text === "unknown") {
    return null;
  }
  const value = Number.parseFloat(text);
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  if (/g(i)?b/.test(text)) {
    return value;
  }
  if (/m(i)?b/.test(text)) {
    return value / MIB_PER_GIB;
  }
  if (/k(i)?b/.test(text)) {
    return value / KIB_PER_GIB;
  }
  // Bare numeric string — fall back to the magnitude bands.
  return normaliseTotalMemoryToGiB(value);
}

/**
 * Classify the current device's memory capacity.
 *
 * An unknown capacity on a TV resolves to "low", and that default is the whole
 * point of this module rather than an oversight. webOS and Tizen run
 * WebKit-derived engines that do not expose `navigator.deviceMemory`, and the
 * LG / Samsung shells frequently leave `deviceTotalMemory` unpopulated — so
 * "Unknown" is the *common* case on precisely the devices whose low-memory
 * kills motivated this classification. Defaulting to "standard" would leave
 * them on the permissive configuration and the adaptation would never apply.
 *
 * A device that does report more than LOW_MEMORY_THRESHOLD_GIB opts back into
 * the standard configuration, so capable TVs are unaffected.
 *
 * Non-TV clients (desktop / mobile browsers) always return "standard"; they do
 * not use the 10-foot strategy this tier sizes.
 *
 * Note this deliberately does NOT branch on isWebOsPlatform() /
 * isTizenPlatform() / isVegOsPlatform(). The conservative unknown-default
 * already routes those devices correctly, and a per-platform table would be
 * speculative tuning with no measurement behind it.
 *
 * @returns "low" for memory-constrained devices, otherwise "standard".
 */
export function getDeviceMemoryTier(): TDeviceMemoryTier {
  if (!DeviceModel.isTvDevice()) {
    return "standard";
  }
  const reported: string | number =
    DeviceModel.getTVProperties().getDeviceTotalMemory();
  const totalGiB: number | null = normaliseTotalMemoryToGiB(reported);
  if (totalGiB === null) {
    return "low";
  }
  return totalGiB <= LOW_MEMORY_THRESHOLD_GIB ? "low" : "standard";
}
